import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import ArticleCard from '@legacy-vfuk/core-article-card';
import Heading from '@legacy-vfuk/core-heading';
import Modal from '@legacy-vfuk/core-modal';
import { OverlayCloseSource } from '@legacy-vfuk/core-overlay-controller';
import SimpleGrid from '@legacy-vfuk/core-simple-grid';
import { navigate } from 'gatsby';
import { getImage, getSrc } from 'gatsby-plugin-image';
import { FC, useEffect, useState } from 'react';
import { Icon } from '@legacy-vfuk/core-icon';

import { useAllowedUris } from '@/hooks';
import { useModalContext } from '@/hooks/modal/useModal';
import { useLogin } from '@/hooks/useLogin';

import { ArticleCardWrapper, ErrorModalCloseButton, ErrorModalWrapper } from './SWErrorModal.styles';
import { ErrorCardProps, SWErrorModalProps } from './SWErrorModal.types';
import usingOpcoUrl from '@/helpers/prefixHelper';

const SWErrorModal: FC<SWErrorModalProps> = ({ richTextHeading, errorCards, closeButtonType = 'outside' }) => {
  const { isErrorModalOpen, toggleModal, errorInfo } = useModalContext();
  const [modalCardsImageSrc, setModalCardsImageSrc] = useState<string | undefined>('');
  const [selectedCard, setSelectedCard] = useState<ErrorCardProps | null>(null);
  const { handleLogin } = useLogin();

  const allowedUris = useAllowedUris();
  const handleCloseCB = (source: OverlayCloseSource | undefined = undefined) => {
    toggleModal(false, 'Error');
    source === 'MODAL_CROSS_CLICK' && allowedUris.isSelectAccountPage && navigate('/');
  };

  useEffect(() => {
    const matchingError = errorCards?.find(
      card =>
        card.requestUrl === errorInfo.requestUrl &&
        card.statusNumber === errorInfo.statusCode &&
        (card.shouldUseApiMessage ? card.apiMessage === errorInfo.statusType : true)
    );
    const defaultCard = errorCards?.find(card => card.name === 'Default Error Card');

    const currentCard = matchingError || defaultCard || errorCards?.[0];
    setSelectedCard(currentCard);
    setModalCardsImageSrc(getSrc(currentCard?.cardImage) ?? getImage(currentCard?.cardImage)?.placeholder?.fallback);
  }, [errorInfo, isErrorModalOpen]);

  const handleButtonClick = () => {
    if (selectedCard?.shouldRedirectToLogin) {
      handleLogin();
    } else {
      usingOpcoUrl(selectedCard?.primaryButton?.url ?? '');
    }
  };

  return (
    <Modal
      id="ErrorModal"
      srName="Error Modal"
      isOpen={isErrorModalOpen}
      size={4}
      animate
      onCloseCb={handleCloseCB}
      isClosable={closeButtonType == 'outside'}
    >
      <ErrorModalWrapper>
        {richTextHeading && (
          <Heading level={3} justify="center">
            {documentToReactComponents(JSON.parse(richTextHeading.raw))}
          </Heading>
        )}

        <SimpleGrid
          columns={{
            sm: 1,
            md: 1,
          }}
          spacing={0}
        >
          <ArticleCardWrapper>
            {closeButtonType == 'inside' && (
              <ErrorModalCloseButton onClick={() => handleCloseCB()} data-testid="error-modal-close-button">
                <Icon group={'system'} name={'close'} size={5} />
              </ErrorModalCloseButton>
            )}

            <ArticleCard
              appearance="primary"
              level={2}
              primaryImage={{
                sm: {
                  src: modalCardsImageSrc ?? '',
                },
                alt: '',
              }}
              primaryButton={{
                text: selectedCard?.primaryButton?.label ?? '',
                onClick: handleButtonClick,
              }}
              headingText={selectedCard?.headingText ?? ''}
              text={selectedCard?.text ?? (selectedCard?.shouldUseApiMessage ? errorInfo.errorMessage : '') ?? ''}
            />
          </ArticleCardWrapper>
        </SimpleGrid>
      </ErrorModalWrapper>
    </Modal>
  );
};

export default SWErrorModal;
